/**
 * BackgroundJobsTray · tray flotante bottom-right con jobs async del usuario.
 *
 * Vive en AppLayout y suscribe a TODOS los UPDATEs de core.background_jobs
 * para el usuario actual. Muestra:
 *   - Jobs running con progress_step + spinner.
 *   - Jobs done con link al recurso creado (prospect/campaign).
 *   - Jobs error con mensaje truncado.
 *
 * El usuario puede cerrar individualmente cada toast — eso solo lo oculta
 * localmente (no borra el job de DB).
 *
 * Realtime + RLS hacen el filtering automático: el usuario solo ve sus jobs.
 */
import { CheckCircle2, Loader2, Sparkles, XCircle, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { useSupabase } from '@/hooks/useSupabase';
import {
  dismissJob,
  listRecentJobs,
  subscribeToUserJobs,
  type BackgroundJob,
} from '@/services/backgroundJobsService';

const VISIBLE_STATUSES: BackgroundJob['status'][] = ['pending', 'running', 'done', 'error'];

export function BackgroundJobsTray() {
  const { session } = useSupabase();
  const userId = session?.user?.id ?? null;
  const [jobs, setJobs] = useState<BackgroundJob[]>([]);

  // Carga inicial + suscripción realtime.
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;

    void listRecentJobs().then((initial) => {
      if (cancelled) return;
      setJobs(initial);
    });

    const channel = subscribeToUserJobs(userId, (updated) => {
      if (cancelled) return;
      setJobs((prev) => {
        // Si dismissed_at != null, sacamos el job del tray inmediatamente
        // (otro tab del mismo user, o el FromJobPage Cancelar, lo descartó).
        if (updated.dismissed_at) {
          return prev.filter((j) => j.id !== updated.id);
        }
        const idx = prev.findIndex((j) => j.id === updated.id);
        if (idx === -1) return [updated, ...prev].slice(0, 10);
        const next = [...prev];
        next[idx] = updated;
        return next;
      });
    });

    return () => {
      cancelled = true;
      void channel.unsubscribe();
    };
  }, [userId]);

  const visibleJobs = useMemo(
    () =>
      jobs.filter(
        (j) => VISIBLE_STATUSES.includes(j.status) && !j.dismissed_at,
      ),
    [jobs],
  );

  const dismiss = useCallback((id: string) => {
    // Optimista: lo sacamos local y persistimos en DB. Si el UPDATE falla,
    // el realtime sync no lo va a re-agregar (porque sigue sin dismissed_at en
    // DB) — pero esto es muy raro, y al refrescar volvería a aparecer.
    setJobs((prev) => prev.filter((j) => j.id !== id));
    void dismissJob(id).catch((err) => {
      console.error('[Tray] dismissJob:', err);
    });
  }, []);

  if (visibleJobs.length === 0) return null;

  return (
    <div className="pointer-events-none fixed bottom-4 right-4 z-50 flex w-[360px] max-w-[calc(100vw-2rem)] flex-col gap-2">
      {visibleJobs.slice(0, 5).map((job) => (
        <JobCard key={job.id} job={job} onDismiss={() => dismiss(job.id)} />
      ))}
    </div>
  );
}

// ─── JobCard ────────────────────────────────────────────────────────────────

interface JobCardProps {
  job: BackgroundJob;
  onDismiss: () => void;
}

function JobCard({ job, onDismiss }: JobCardProps) {
  const isRunning = job.status === 'running' || job.status === 'pending';
  const isDone = job.status === 'done';
  const isError = job.status === 'error';

  const title = JOB_KIND_LABEL[job.kind] ?? job.kind;
  const subtitle = isRunning
    ? job.progress_step ?? 'Procesando…'
    : isDone
      ? 'Listo'
      : isError
        ? job.error_json?.message?.slice(0, 100) ?? 'Falló'
        : '';

  // Link contextual al recurso creado / al review page del job.
  // analyze_prospect_deep[_v2] no crea el prospect directamente — abre form review.
  const isDeepResearch =
    job.kind === 'analyze_prospect_deep' || job.kind === 'analyze_prospect_deep_v2';
  const link = isDone
    ? isDeepResearch
      ? `/prospects/new/from-job/${job.id}`
      : job.related_prospect_id
        ? `/prospects/${job.related_prospect_id}`
        : job.related_campaign_id
          ? `/campaigns/${job.related_campaign_id}`
          : null
    : null;

  const linkLabel =
    isDeepResearch && isDone ? 'Revisar y crear prospect' : 'Abrir resultado';

  return (
    <div
      className={`pointer-events-auto border-border bg-card flex items-start gap-3 rounded-lg border p-3 shadow-lg ${
        isError ? 'border-destructive/40' : isDone ? 'border-emerald-500/40' : ''
      }`}
    >
      <div
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
          isRunning
            ? 'bg-primary/15 text-primary'
            : isDone
              ? 'bg-emerald-500/15 text-emerald-400'
              : 'bg-destructive/15 text-destructive'
        }`}
      >
        {isRunning ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : isDone ? (
          <CheckCircle2 className="h-4 w-4" />
        ) : isError ? (
          <XCircle className="h-4 w-4" />
        ) : (
          <Sparkles className="h-4 w-4" />
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <p className="text-foreground truncate text-caption font-semibold">{title}</p>
          <button
            type="button"
            onClick={onDismiss}
            className="text-muted-foreground hover:text-foreground -mr-1 -mt-1 rounded p-0.5"
            aria-label="Cerrar"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
        <p className="text-muted-foreground mt-0.5 break-words text-caption leading-relaxed">
          {subtitle}
        </p>
        {isRunning && typeof job.progress_pct === 'number' ? (
          <div className="bg-muted mt-2 h-1 w-full overflow-hidden rounded-full">
            <div
              className="bg-primary h-full transition-all duration-300"
              style={{ width: `${job.progress_pct}%` }}
            />
          </div>
        ) : null}
        {link ? (
          <Link
            to={link}
            className="bg-primary/12 text-primary hover:bg-primary/18 border-primary/25 mt-2 inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-micro font-semibold transition-colors"
          >
            {linkLabel} →
          </Link>
        ) : null}
      </div>
    </div>
  );
}

const JOB_KIND_LABEL: Record<BackgroundJob['kind'], string> = {
  analyze_prospect_deep: 'Análisis profundo de prospect',
  analyze_prospect_deep_v2: 'Deep Research v2 de prospect',
  generate_campaign_deep: 'Generando campaña IA',
  bulk_prospect_enrichment: 'Enriqueciendo prospects en lote',
};
